import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import { ApiRouteRequestError, PlatformError } from '~/utils/errors'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Account } from '@reward-platform/ancillaries-schemas/accounts'
import useErrorMessages from '~/hooks/useMessages/useErrorMessages'
import { getAccount } from '~/services/accountsService'
import { usePlatformTheme } from '~/context/theme'
import { useAuthContext } from '../auth'
import { useNotification } from '../notification'

export type AccountContextValue = {
  account?: Account
  householdAccount?: Account
  aviosBalance?: number
  householdAviosBalance?: number
  isLoading?: boolean
  isError?: boolean
  refetch?: () => Promise<void>
}

const ACCOUNT_KEY = 'getAccount'
const HOUSEHOLD_ACCOUNT_KEY = 'getHouseholdAccount'

export const AccountContext = createContext<AccountContextValue | undefined>(undefined)

export const AccountProvider = ({ children }: PropsWithChildren): ReactElement => {
  const { isAuthenticated, userId } = useAuthContext()
  const queryClient = useQueryClient()
  const { addNotification } = useNotification()
  const { isAviosTheme } = usePlatformTheme()

  const {
    data: account,
    isLoading,
    error,
  } = useQuery<Account, ApiRouteRequestError>({
    queryKey: [ACCOUNT_KEY, userId],
    queryFn: () => getAccount('REWARD'),
    enabled: isAuthenticated,
    meta: {
      notifyUser: false,
      errorMessage: 'Accounts Error - could not get account details',
    },
  })

  const {
    data: householdAccount,
    isLoading: isLoadingHouseholdAccount,
    error: errorHouseholdAccount,
  } = useQuery<Account, ApiRouteRequestError>({
    queryKey: [HOUSEHOLD_ACCOUNT_KEY, userId],
    queryFn: () => getAccount('TOTAL_REWARD'),
    enabled: isAuthenticated,
    meta: {
      notifyUser: false,
      errorMessage: 'Accounts Error - could not get household account details',
    },
  })

  const { getErrorCodeMessage } = useErrorMessages()

  useEffect(() => {
    if (errorHouseholdAccount?.code && !isAviosTheme) {
      const errorMessage = getErrorCodeMessage(errorHouseholdAccount?.code)
      addNotification('ACCOUNT_CONTEXT_ERROR', errorMessage, {
        type: 'ERROR',
        action: 'RELOAD',
      })
    }
  }, [errorHouseholdAccount?.code, addNotification, isAviosTheme, getErrorCodeMessage])

  const contextValue = useMemo<AccountContextValue>(
    () => ({
      account,
      householdAccount,
      aviosBalance: account?.balance.amount,
      householdAviosBalance: householdAccount?.balance.amount,
      isLoading: isLoading || isLoadingHouseholdAccount,
      isError: !!error || !!errorHouseholdAccount,
      refetch: () =>
        queryClient.invalidateQueries({ queryKey: [ACCOUNT_KEY, HOUSEHOLD_ACCOUNT_KEY] }),
    }),
    [
      account,
      error,
      errorHouseholdAccount,
      householdAccount,
      isLoading,
      isLoadingHouseholdAccount,
      queryClient,
    ]
  )

  return <AccountContext.Provider value={contextValue}>{children}</AccountContext.Provider>
}

export function useAccount(): AccountContextValue {
  const context = useContext(AccountContext)
  if (!context) {
    throw new PlatformError('useAccount must be used within a AccountProvider')
  }
  return context
}
